import { Ban, CheckCircle2, ClipboardCheck, FileClock, UserMinus, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import {
  ATTRITION_CLEARANCE,
  ATTRITION_REVIEW,
  ATTRITION_STATUS,
  type AttritionRequestDetail,
  type AttritionRequestDocument,
} from '../../../human-resources/attrition/attritionTypes';
import { UploadAttritionDocumentModal } from '../../../human-resources/attrition/components/AttritionActionModals';
import { AttritionClearancesCard } from '../../../human-resources/attrition/components/AttritionClearancesCard';
import { AttritionDocumentPreviewModal } from '../../../human-resources/attrition/components/AttritionDocumentPreviewModal';
import { AttritionDocumentsCard } from '../../../human-resources/attrition/components/AttritionDocumentsCard';
import { myAttritionRequestsApi } from '../myAttritionRequestsApi';

type TabKey = 'info' | 'documents' | 'clearances';

type Pending =
  | { kind: 'upload'; document: AttritionRequestDocument }
  | { kind: 'preview'; document: AttritionRequestDocument }
  | null;

/**
 * One attrition request filed for the reader, on the header layout — HR's profile, narrowed to
 * what the employee does: read where it stands and upload the documents it asks of them.
 *
 * **The same checklist card, upload form and preview as HR's page**, handed this surface's own
 * endpoints and its own key (`my-attrition-requests.upload`) — two copies would drift. **No
 * Review, no Edit, no Cancel, no Delete**: those are HR's and the reviewers'. The CLEARANCES are the
 * same card READ-ONLY (no `onDecide`) — what each unit says the employee still owes is theirs to read.
 */
export function MyAttritionRequestProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [request, setRequest] = useState<AttritionRequestDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      myAttritionRequestsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setRequest(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this request.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Self Service',
    'Human Resources',
    { label: 'Attrition', to: '/self-service/attrition-requests' },
    request ? request.requestNumber : 'Request',
  ];

  if (error && !request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ProfileHeaderSkeleton tabs={3} />
      </div>
    );
  }

  const record = request;
  const banner = bannerFor(record);

  async function download(document: AttritionRequestDocument) {
    try {
      const { blob } = await myAttritionRequestsApi.downloadDocument(record.id, document.id);
      saveBlob(blob, document.fileName ?? document.name);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download that document.');
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="my-attrition-request-profile"
        header={
          <ProfileHeader
            id="my-attrition-request-profile-header"
            icon={<UserMinus className="h-6 w-6" aria-hidden="true" />}
            // The reader IS the employee, so the request's own number and kind identify it.
            title={record.requestNumber}
            meta={[record.categoryName]}
            status={
              <span className="flex items-center gap-2">
                <StatusText tone="inactive">{record.statusLabel}</StatusText>
                <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                  {record.isActive ? 'Active' : 'Deleted'}
                </StatusText>
              </span>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // The default cog: History Logs alone, which HIDES without `audit-logs.read` — as an employee will have.
            settings={{ onHistory: () => setHistoryOpen(true) }}
          />
        }
        banner={
          <NextActionAlert
            error={Boolean(error)}
            id="my-attrition-request-banner"
            icon={<banner.icon className="h-4 w-4" aria-hidden="true" />}
            title={error ? 'This page may be out of date' : banner.title}
            description={error ? error.message : banner.description}
            action={
              record.isOpen && tab !== 'documents' ? (
                <Button
                  id="my-attrition-request-open-documents"
                  variant={banner.owed > 0 ? 'primary' : 'secondary'}
                  leading={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
                  onClick={() => setTab('documents')}
                >
                  {banner.owed > 0 ? 'Upload documents' : 'Documents'}
                </Button>
              ) : null
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'documents',
            label: (
              <>
                Documents <CountBadge count={record.documents.length} />
              </>
            ),
          },
          {
            key: 'clearances',
            label: (
              <>
                Clearances <CountBadge count={record.clearances.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {tab === 'clearances' ? (
          <AttritionClearancesCard record={record} busy={false} />
        ) : tab === 'documents' ? (
          <AttritionDocumentsCard
            record={record}
            busy={false}
            uploadPermission="my-attrition-requests.upload"
            onUpload={(document) => setPending({ kind: 'upload', document })}
            onOpen={(document) => setPending({ kind: 'preview', document })}
          />
        ) : (
          <>
            {/* HR's record of the request — no Edit: the employee changes none of it. */}
            <ProfileCard id="my-attrition-request-profile-request" title="Request">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Request number', value: record.requestNumber, mono: true },
                  { label: 'Category', value: record.categoryName },
                  {
                    label: 'Effective date',
                    value: record.effectiveDate != null ? formatDateOnly(record.effectiveDate) : null,
                    mono: true,
                  },
                  // Who filed it and when is the header's Created-by line — not printed twice.
                  { label: 'Remarks', value: record.remarks },
                ]}
              />
            </ProfileCard>

            {record.status === ATTRITION_STATUS.cancelled ? (
              <ProfileCard id="my-attrition-request-profile-cancellation" title="Cancellation">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'Reason', value: record.cancelReason },
                    { label: 'Cancelled by', value: record.cancelledByName },
                  ]}
                />
              </ProfileCard>
            ) : null}
          </>
        )}
      </ProfileHeaderShell>

      {pending?.kind === 'upload' ? (
        <UploadAttritionDocumentModal
          request={record}
          document={pending.document}
          upload={myAttritionRequestsApi.uploadDocument}
          onClose={() => setPending(null)}
          onSaved={(next) => {
            setRequest(next);
            setPending(null);
            toast.success(`${pending.document.name} was submitted for review.`);
          }}
        />
      ) : null}

      {pending?.kind === 'preview' ? (
        <AttritionDocumentPreviewModal
          requestId={record.id}
          document={pending.document}
          download={myAttritionRequestsApi.downloadDocument}
          onDownload={() => void download(pending.document)}
          onClose={() => setPending(null)}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="my-attrition-requests"
        entityId={record.id}
      />
    </div>
  );
}

/** One banner for every state, in the employee's terms — what is still owed by them. */
function bannerFor(record: AttritionRequestDetail): {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
  title: string;
  description: string;
  owed: number;
} {
  const owed = record.documents.filter(
    (d) => d.reviewStatus === ATTRITION_REVIEW.notSubmitted || d.reviewStatus === ATTRITION_REVIEW.returned,
  ).length;
  const returned = record.documents.filter((d) => d.reviewStatus === ATTRITION_REVIEW.returned).length;
  const notCleared = record.clearances.filter((c) => c.status === ATTRITION_CLEARANCE.notCleared).length;

  if (!record.isActive) {
    return { icon: Ban, title: 'Deleted', description: 'HR has removed this request.', owed: 0 };
  }

  switch (record.status) {
    case ATTRITION_STATUS.pendingDocuments:
      return {
        icon: FileClock,
        title: 'Documents needed from you',
        description:
          `${owed} ${owed === 1 ? 'document still needs' : 'documents still need'} a file` +
          (returned > 0 ? `, ${returned} of them returned — the reason is on the document.` : '.'),
        
        owed,
      };
    case ATTRITION_STATUS.forReview:
      return {
        icon: ClipboardCheck,
        title: 'With the reviewers',
        description:
          notCleared > 0
            ? `${notCleared} ${notCleared === 1 ? 'unit has' : 'units have'} not cleared you yet — what is still owed is on the Clearances tab.`
            : 'Every document is submitted. A document can still be replaced until it is approved.',
        
        owed,
      };
    case ATTRITION_STATUS.completed:
      return {
        icon: CheckCircle2,
        title: 'Completed',
        description:
          record.clearances.length > 0
            ? 'Every document is approved and every unit has cleared you. Nothing more is needed from you.'
            : 'Every document is approved. Nothing more is needed from you.',
        
        owed: 0,
      };
    case ATTRITION_STATUS.cancelled:
      return {
        icon: XCircle,
        title: 'Cancelled',
        description: `This request will not go forward: ${record.cancelReason ?? ''}`,
        
        owed: 0,
      };
    default:
      return { icon: FileClock, title: record.statusLabel, description: '', owed };
  }
}
